import { alpha, Box, CircularProgress, LinearProgress, Typography } from '@mui/material';
import type { SxProps } from '@mui/system';
import { AnimationProps, motion } from 'framer-motion';
import React, { FC, memo, useEffect } from 'react';

import icons from '../../../constants/icons';
import palette from '../../../theme/learnerPalette';

export type AtomProgressType = 'linear' | 'circular';
export type AtomProgressColor = 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
export type AtomProgressLayout = 'overlay' | 'inline' | 'inline-silent';
export type AtomProgressBackground = 'white' | 'prussianBlue' | 'transparent' | 'grey';
export type AtomProgressTextColor = 'white' | 'prussianBlue';
export type AtomProgressIconVariant = 'light' | 'dark' | 'small';

export interface IAtomProgressProps {
  type?: AtomProgressType;
  color?: AtomProgressColor;
  value?: number;
  size?: number;
  thickness?: number;
  showLabel?: boolean;
  label?: string;
  customSx?: SxProps;
  visible?: boolean;
  layout?: AtomProgressLayout;
  background?: AtomProgressBackground;
  loadingText?: string;
  subtitle?: string;
  textColor?: AtomProgressTextColor;
  paddingTop?: string;
  iconVariant?: AtomProgressIconVariant;
  announceText?: string;
  'data-testid'?: string;
}

const ICON_SIZE_SMALL = 35;
const ICON_SIZE_DEFAULT = 45;
const ICON_MARGIN_BOTTOM = 20;
const LOADER_TEXT_FONT_SIZE = '1rem';

const resolveBackground = (bg?: AtomProgressBackground): string => {
  switch (bg) {
    case 'grey':
      return palette.light.background.alabaster;

    case 'white':
      return palette.light.background.white;

    case 'transparent':
      return palette.light.background.transparent;

    case 'prussianBlue':
      return alpha(palette.light.background.prussianBlue, 0.75);

    default:
      return 'unset';
  }
};

const resolveTextColor = (tc?: AtomProgressTextColor): string => {
  if (tc === 'white') {
    return palette.light.text.white;
  }

  return palette.light.text.prussianBlue;
};

const resolveIconSrc = (variant?: AtomProgressIconVariant): string => {
  if (variant === 'light') {
    return icons.CIRCULAR_LOADER_LIGHT;
  }

  return icons.CIRCULAR_LOADER_DARK;
};

const resolveIconSize = (variant?: AtomProgressIconVariant): number =>
  variant === 'small' ? ICON_SIZE_SMALL : ICON_SIZE_DEFAULT;

const isLoaderMode = (props: IAtomProgressProps): boolean => props.visible !== undefined || props.layout !== undefined;

const loaderAnimationProps: AnimationProps = {
  animate: { rotate: -360 },
  transition: {
    repeat: Infinity,
    duration: 2,
    ease: 'linear',
  },
};

const AtomProgressBase: FC<IAtomProgressProps> = (props) => {
  const {
    type = 'linear',
    color = 'primary',
    value,
    size = 40,
    thickness = 4,
    showLabel = false,
    label,
    customSx,
    visible,
    layout,
    background,
    loadingText,
    subtitle,
    textColor,
    paddingTop,
    iconVariant,
    announceText,
    'data-testid': dataTestId,
  } = props;

  const inLoaderMode = isLoaderMode(props);
  const isVisible = visible ?? false;
  const isSilent = layout === 'inline-silent';
  const isInline = layout === 'inline' || isSilent;

  useEffect(() => {
    if (!inLoaderMode || !isVisible || !announceText) {
      return undefined;
    }

    const el = document.createElement('div');

    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    el.style.cssText = [
      'position:absolute !important',
      'left:-10000px !important',
      'top:auto !important',
      'width:1px !important',
      'height:1px !important',
      'overflow:hidden !important',
      'clip:rect(1px,1px,1px,1px) !important',
      'white-space:nowrap !important',
      'border:0 !important',
      'padding:0 !important',
      'margin:0 !important',
    ].join(';');
    el.textContent = announceText;
    document.body.appendChild(el);

    return () => {
      el.remove();
    };
  }, [inLoaderMode, isVisible, announceText]);

  if (inLoaderMode) {
    if (!isVisible) {
      return null;
    }

    return (
      <Box
        data-testid={dataTestId}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: isInline ? 'unset' : 'absolute',
          inset: 0,
          zIndex: isInline ? 1 : 2,
          background: resolveBackground(background),
          color: resolveTextColor(textColor),
          ...(paddingTop === undefined ? {} : { paddingTop }),
          ...customSx,
        }}
      >
        <Box
          component={motion.img as React.ElementType}
          src={resolveIconSrc(iconVariant)}
          alt=""
          sx={{
            marginBottom: `${ICON_MARGIN_BOTTOM}px`,
            width: resolveIconSize(iconVariant),
            height: resolveIconSize(iconVariant),
          }}
          {...loaderAnimationProps}
        />

        {!isSilent && loadingText && (
          <Typography
            aria-hidden="true"
            className="atom-progress-text"
            sx={{ fontSize: LOADER_TEXT_FONT_SIZE, textAlign: 'center' }}
          >
            {loadingText}
          </Typography>
        )}

        {!isSilent && subtitle && (
          <Typography aria-hidden="true" sx={{ fontSize: LOADER_TEXT_FONT_SIZE, textAlign: 'center' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    );
  }

  const variant = value === undefined ? 'indeterminate' : 'determinate';

  if (type === 'circular') {
    return (
      <Box
        data-testid={dataTestId}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          ...customSx,
        }}
      >
        <CircularProgress color={color} variant={variant} value={value} size={size} thickness={thickness} />
        {showLabel && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" component="div" color="text.secondary">
              {label ?? `${Math.round(value ?? 0)}%`}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box data-testid={dataTestId} sx={{ width: '100%' }}>
      {showLabel && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {label ?? 'Progress'}
          </Typography>
          {value !== undefined && (
            <Typography variant="body2" color="text.secondary">
              {`${Math.round(value)}%`}
            </Typography>
          )}
        </Box>
      )}
      <LinearProgress color={color} variant={variant} value={value} sx={{ height: thickness, ...customSx }} />
    </Box>
  );
};

AtomProgressBase.displayName = 'AtomProgress';

const AtomProgress = memo(AtomProgressBase);

export default AtomProgress;
export { AtomProgress };
